import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { chatWidgetTroubleshootingLastResultSelector } from '@mm/state/selectors/chatWidgetTroubleshootingSelectors';
import { globalJavascriptBaseUrlSelector } from '@mm/state/selectors/envSelectors';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { Result } from '../ChatWidgetTroubleshootingToolResult';
import { Button } from '@mm/components';
import { StoreState } from '@mm/state/store-interfaces';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';

type Props = {
  isXRay?: boolean;
};

export const useJSLoadedResult = (props: Props): Result => {
  const { isXRay } = props;
  const [expanded, setExpanded] = useState(false);
  const result = useSelector(chatWidgetTroubleshootingLastResultSelector);
  const connectedPage = useSelector(
    (state: StoreState) =>
      state.connectedPage.entities[0] || activeFacebookPageSelector(state),
  );
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const facebookPage = activeFacebookPage || connectedPage;
  const globalJavascriptBaseUrl = useSelector(globalJavascriptBaseUrlSelector);

  const { pixelName: whitelabelPixelName } = useWhitelabelConfig();
  const pixelName = whitelabelPixelName
    ? `${whitelabelPixelName} Lead Generator`
    : 'X-Ray Lead Generation Pixel';

  if (!result || !facebookPage) {
    return;
  }

  const infoTooltip = (
    <div className="chat-widget-troubleshooting-tool-result__more-info">
      The following script
      {result.globalJS.urlsLoaded.length > 1 ? 's were' : ' was'} loaded:{' '}
      <ul>
        {result.globalJS.urlsLoaded.map(url => (
          <li key={url}>
            <code>{url}</code>
          </li>
        ))}
      </ul>
      {result.globalJS.correctPageId === false && (
        <>
          Please install this script instead:
          <ul>
            <li>
              <code>
                https://{globalJavascriptBaseUrl}/js/{facebookPage.remoteId}.js
              </code>
            </li>
          </ul>
        </>
      )}
    </div>
  );

  if (!result.globalJS.loaded) {
    return {
      result: 'error',
      description: isXRay
        ? `${pixelName} not detected`
        : 'Chat widget script not detected',
      infoTooltip: (
        <>
          Please install the {isXRay ? `${pixelName}` : 'chat widget'} script
          inside the <code>&lt;head&gt;</code> tag on every page on your website
        </>
      ),
    };
  }
  if (result.globalJS.correctPageId === false) {
    return {
      result: 'error',
      description: (
        <>
          Incorrect {isXRay ? `${pixelName}` : 'chat widget'} script detected
          <Button
            color="transparent"
            css={{
              container:
                'chat-widget-troubleshooting-tool-result__toggle-button',
            }}
            onClick={() => setExpanded(s => !s)}
          >
            {expanded ? 'Hide' : 'Show'} Details
          </Button>
          {expanded && infoTooltip}
        </>
      ),
    };
  }
  if (result.globalJS.loadedMoreThanOnce) {
    return {
      result: 'error',
      description: (
        <>
          {isXRay ? `${pixelName}` : 'Chat widget script'} loaded multiple times
          <Button
            color="transparent"
            css={{
              container:
                'chat-widget-troubleshooting-tool-result__toggle-button',
            }}
            onClick={() => setExpanded(s => !s)}
          >
            {expanded ? 'Hide' : 'Show'} Details
          </Button>
          {expanded && infoTooltip}
        </>
      ),
    };
  }
  return {
    result: 'ok',
    description: isXRay
      ? `${pixelName} detected`
      : 'Chat widget script detected',
  };
};
